import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Send, Settings, Users, RefreshCw, Calendar, Mail, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  getReminderConfig, 
  updateReminderConfig, 
  getPendingReminders, 
  sendAllReminders,
  resetReminderCount,
  getAdminMe 
} from '@/lib/api';

const AdminReminders = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [config, setConfig] = useState({
    reminder_enabled: false,
    reminder_start_date: '',
    reminder_deadline: '2026-07-01',
    reminder_frequency_days: 7,
    reminder_max_count: 3
  });
  const [pendingGuests, setPendingGuests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [lastSendResult, setLastSendResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const [configRes, pendingRes] = await Promise.all([
        getReminderConfig(),
        getPendingReminders()
      ]);
      setConfig(configRes.data);
      setPendingGuests(pendingRes.data.guests || []);
      setPendingCount(pendingRes.data.count || 0);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await updateReminderConfig(config);
      toast.success('Configuration sauvegardée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSendAllReminders = async () => {
    setConfirmDialog(false);
    setSending(true);
    try {
      const response = await sendAllReminders();
      setLastSendResult(response.data);
      toast.success(`${response.data.sent} relance(s) envoyée(s)`);
      loadData();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'envoi des relances');
      }
    } finally {
      setSending(false);
    }
  };

  const handleResetReminder = async (rsvpId, name) => {
    try {
      await resetReminderCount(rsvpId);
      toast.success(`Compteur réinitialisé pour ${name}`);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Relances automatiques">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Relances automatiques">
      <div className="space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Invités en attente</p>
                    <p className="text-3xl font-bold text-gray-900">{pendingCount}</p>
                    <p className="text-xs text-gray-400 mt-1">à relancer</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-50">
                    <Users className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <p className="text-xl font-bold text-gray-900">
                      {config.reminder_enabled ? 'Activé' : 'Désactivé'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {config.reminder_enabled ? 'Relances automatiques actives' : 'Système en pause'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${config.reminder_enabled ? 'bg-green-50' : 'bg-gray-100'}`}>
                    {config.reminder_enabled ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Fréquence</p>
                    <p className="text-3xl font-bold text-gray-900">{config.reminder_frequency_days}</p>
                    <p className="text-xs text-gray-400 mt-1">jours entre chaque relance</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Last Send Result */}
        {lastSendResult && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Card className="border-green-200 bg-green-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-800">Dernière campagne de relances</h3>
                    <p className="text-green-700">
                      <span className="font-bold">{lastSendResult.sent}</span> envoyée(s), 
                      <span className="font-bold ml-2">{lastSendResult.skipped}</span> ignorée(s),
                      <span className="font-bold ml-2">{lastSendResult.failed}</span> échouée(s)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-terracotta" />
              Configuration des relances
            </CardTitle>
            <CardDescription>
              Définissez les paramètres du système de relance automatique
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="reminder_enabled" className="text-base font-medium">
                  Activer les relances automatiques
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Permet d'envoyer des emails de rappel aux invités n'ayant pas répondu
                </p>
              </div>
              <Switch
                id="reminder_enabled"
                checked={config.reminder_enabled}
                onCheckedChange={(checked) => setConfig({ ...config, reminder_enabled: checked })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Deadline */}
              <div className="space-y-2">
                <Label htmlFor="reminder_deadline">Date limite de réponse</Label>
                <Input
                  id="reminder_deadline"
                  type="date"
                  value={config.reminder_deadline}
                  onChange={(e) => setConfig({ ...config, reminder_deadline: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  Date affichée dans les emails de relance
                </p>
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label htmlFor="reminder_frequency">Fréquence (jours)</Label>
                <Input
                  id="reminder_frequency"
                  type="number"
                  min="1"
                  max="30"
                  value={config.reminder_frequency_days}
                  onChange={(e) => setConfig({ ...config, reminder_frequency_days: parseInt(e.target.value) || 7 })}
                />
                <p className="text-xs text-gray-500">
                  Nombre de jours minimum entre deux relances
                </p>
              </div>

              {/* Max count */}
              <div className="space-y-2">
                <Label htmlFor="reminder_max">Nombre maximum de relances</Label>
                <Input
                  id="reminder_max"
                  type="number"
                  min="1"
                  max="10"
                  value={config.reminder_max_count}
                  onChange={(e) => setConfig({ ...config, reminder_max_count: parseInt(e.target.value) || 3 })}
                />
                <p className="text-xs text-gray-500">
                  Chaque invité recevra au maximum ce nombre de relances
                </p>
              </div>

              {/* Start date */}
              <div className="space-y-2">
                <Label htmlFor="reminder_start">Date de début des relances</Label>
                <Input
                  id="reminder_start"
                  type="date"
                  value={config.reminder_start_date || ''}
                  onChange={(e) => setConfig({ ...config, reminder_start_date: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  Optionnel : les relances ne commenceront qu'après cette date
                </p>
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                onClick={handleSaveConfig} 
                disabled={saving}
                className="bg-terracotta hover:bg-terracotta/90"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  'Sauvegarder la configuration'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Send Reminders Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-terracotta" />
              Envoyer les relances
            </CardTitle>
            <CardDescription>
              Envoyez des emails de rappel aux invités n'ayant pas encore confirmé leur présence
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    {pendingCount} invité(s) peuvent recevoir une relance
                  </p>
                  <p className="text-sm text-amber-700">
                    {config.reminder_enabled 
                      ? 'Le système est prêt à envoyer les emails'
                      : 'Activez le système pour pouvoir envoyer les relances'
                    }
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setConfirmDialog(true)}
                disabled={!config.reminder_enabled || pendingCount === 0 || sending}
                className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer toutes les relances
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pending Guests Table */}
        {pendingGuests.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-terracotta" />
                Liste des invités à relancer ({pendingGuests.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className="text-center">Relances envoyées</TableHead>
                      <TableHead>Dernière relance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingGuests.map((guest) => (
                      <TableRow key={guest.id}>
                        <TableCell className="font-medium">
                          {guest.first_name} {guest.family_name}
                        </TableCell>
                        <TableCell className="text-gray-600">{guest.email}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={guest.reminder_count >= config.reminder_max_count ? 'secondary' : 'outline'}>
                            {guest.reminder_count} / {config.reminder_max_count}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-500 text-sm">
                          {formatDate(guest.last_reminder_sent)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetReminder(guest.id, `${guest.first_name} ${guest.family_name}`)}
                            title="Réinitialiser le compteur"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">Comment fonctionne le système de relances ?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Les relances sont envoyées uniquement aux invités qui n'ont pas encore confirmé</li>
                  <li>Chaque invité reçoit au maximum <strong>{config.reminder_max_count}</strong> relances</li>
                  <li>Un délai minimum de <strong>{config.reminder_frequency_days}</strong> jours est respecté entre chaque relance</li>
                  <li>Les emails contiennent un lien direct vers le formulaire de confirmation</li>
                  <li>Vous pouvez réinitialiser le compteur d'un invité si nécessaire</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer l'envoi des relances</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point d'envoyer un email de relance à <strong>{pendingCount}</strong> invité(s).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              Cette action enverra un email personnalisé à chaque invité qui n'a pas encore confirmé sa présence 
              et qui n'a pas atteint le nombre maximum de relances.
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendAllReminders}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Envoyer les relances
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminReminders;
